/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from'react';
import Link from"./Link";
import { cn } from"@/shared/ui/utils/tailwind";
import { resolveSx, SxProps } from"@/shared/ui/utils/sx";

export type ButtonVariant =
  |"solid"
  |"filled"
  |"outlined"
  |"outline"
  |"ghost"
  |"ghost-text"
  |"link"
  |"soft"
  |"subtle"
  |"none";

export type ButtonProps = {
  variant?: ButtonVariant;
  size?:"micro" |"xs" |"sm" |"md" |"lg" |"xl" |"cta" |"small" |"medium" |"large" |"custom";
  color?: string;
  disabled?: boolean;
  loading?: boolean;
  disableSize?: boolean;
  disableClickAnimation?: boolean;
  preventDefault?: boolean;
  fixHover?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onChange?: (e: React.FormEvent<HTMLInputElement>) => void;
  fullWidth?: boolean;
  addOutline?: boolean;
  disableEffects?: boolean;
  type?:"button" |"submit" |"reset";
  backgroundVariant?: number;
  selected?: boolean;
  icon?: any;
  radius?: string | number;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  as?: any;
  component?: any;
  href?: string;
  forwardRef?: React.Ref<HTMLElement> | null;
  styles?: React.CSSProperties | { root?: React.CSSProperties & Record<string, any> };
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
} & SxProps & Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick' |'onChange' |'type' |'color' |'style' |'className' |'children'
>;

const sizeClasses = {
  micro:"h-7 px-2 text-xs",
  xs:"h-8 px-3 text-xs",
  sm:"h-9 px-4 text-sm",
  md:"h-10 px-5 text-sm",
  lg:"h-11 px-6 text-base",
  xl:"h-12 px-8 text-base",
  cta:"h-11 px-6 text-sm tracking-[var(--tracking-wide)]",
  small:"h-9 px-4 text-sm",
  medium:"h-10 px-5 text-sm",
  large:"h-11 px-6 text-base",
  custom:"",
};

const SOLID_CLASSES ="bg-[var(--button-primary-bg)] text-[var(--button-primary-text)] hover:bg-[var(--button-primary-hover-bg)] active:bg-[var(--button-primary-active-bg)]";
const OUTLINED_CLASSES ="bg-[var(--button-secondary-bg)] border border-[var(--button-secondary-border)] text-[var(--button-secondary-text)] hover:bg-[var(--button-secondary-hover-bg)]";

const variantClasses: Record<string, string> = {
  solid: SOLID_CLASSES,
  filled: SOLID_CLASSES,
  outlined: OUTLINED_CLASSES,
  outline: OUTLINED_CLASSES,
  ghost:"bg-transparent border border-[var(--border-subtle)] hover:bg-[var(--surface-hover)]",
  "ghost-text":"bg-transparent border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]",
  link:"bg-transparent text-[var(--seed-base)] hover:underline",
  soft:"bg-[var(--surface-secondary)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)]",
  subtle:"bg-transparent border border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)]",
  none:"",
};

/**
 * Button component with Tailwind styling
 * Replaces Mantine Button
 */
const Button: React.FC<ButtonProps> = ({
  children,
  variant ="solid",
  size ="sm",
  color,
  disabled,
  type ="button",
  fullWidth = false,
  loading,
  onClick,
  style,
  styles: stylesProp,
  as,
  component,
  className,
  selected,
  disableSize = false,
  radius,
  leftSection,
  rightSection,
  icon,
  backgroundVariant,
  addOutline,
  disableEffects,
  disableClickAnimation,
  preventDefault,
  fixHover,
  forwardRef,
  sx,
  m, mt, mb, ml, mr, mx, my,
  p, pt, pb, pl, pr, px, py,
  ...props
}) => {
  const Component = component || as ||"button";
  const sizeClass = disableSize ?"" : sizeClasses[size] || sizeClasses.sm;
  const variantClass = variant in variantClasses ? variantClasses[variant] : variantClasses.solid;

  const resolvedStyles = stylesProp && typeof stylesProp ==='object' &&'root' in stylesProp
    ? (stylesProp as { root?: React.CSSProperties }).root
    : stylesProp as React.CSSProperties | undefined;

  const radiusStyle: React.CSSProperties = radius !== undefined
    ? { borderRadius: typeof radius ==='number' ?`${radius}px` : radius }
    : {};

  const sxStyle = resolveSx({
    sx, m, mt, mb, ml, mr, mx, my, p, pt, pb, pl, pr, px, py,
  });

  const colorStyle: React.CSSProperties = color ? { color } : {};

  return (
    <Component
      ref={forwardRef}
      type={Component ==="button" ? type : undefined}
      disabled={disabled || loading}
      onClick={onClick}
      className={cn(
        "inline-flex items-center justify-center rounded-(--radius-md) transition-colors duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--button-primary-bg) focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50",
        (leftSection || rightSection) &&"gap-(--space-2)",
        fullWidth &&"w-full",
        sizeClass,
        variantClass,
        selected &&"bg-[var(--selection-selected)]",
        loading &&"cursor-wait",
        className
      )}
      style={{ ...resolvedStyles, ...colorStyle, ...radiusStyle, ...sxStyle, ...style }}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {leftSection}
      {children}
      {rightSection}
    </Component>
  );
};

export type ButtonLinkProps = ButtonProps & { href: string };
export const ButtonLink: React.FC<ButtonLinkProps> = ({
  href,
  children,
  ...props
}) => {
  return (
    <Link href={href} onlyBehavior>
      <Button {...props}>{children}</Button>
    </Link>
  );
};

export default Button;
